import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

export default function VisionMission() {
  return (
    <div className="relative min-h-screen bg-[var(--page-bg)] overflow-hidden">
      {/* Soft background accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-20 -left-32 w-[600px] h-[600px] rounded-full blur-3xl opacity-20 bg-[radial-gradient(circle_at_center,rgba(14,98,173,.25),transparent_60%)]" />
        <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-25 bg-[radial-gradient(circle_at_center,rgba(30,138,61,.25),transparent_58%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-10 py-10 md:py-14">
        {/* Breadcrumb + Page Title */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 md:mb-8"
        >
          <nav className="text-xs md:text-sm text-neutral-500 flex gap-2 md:gap-3">
            <Link to="/" className="hover:text-[var(--gov-blue)]">Home</Link>
            <span>/</span>
            <span>About Society</span>
            <span>/</span>
            <span className="text-[var(--gov-blue)] font-semibold">Vision & Mission</span>
          </nav>
          <h1 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight text-[var(--gov-navy)]">
            Vision & Mission
          </h1>
          <p className="mt-2 text-sm md:text-base text-neutral-600 max-w-2xl">
            Sustainable growth for farmers through transparent, technology-driven cooperative systems across Gujarat & Uttar Pradesh.
          </p>
        </motion.div>

        {/* Vision Card */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="relative rounded-2xl border border-neutral-200 bg-white/80 backdrop-blur-md shadow-[0_12px_40px_rgba(0,0,0,0.08)] overflow-hidden mb-8"
        >
          <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[var(--gov-green)] via-emerald-500 to-[var(--gov-blue)]" />
          <div className="p-6 md:p-10">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🌿</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[var(--gov-green)]">Our Vision</h2>
            </div>
            <p className="mt-4 text-[15px] md:text-lg leading-relaxed text-neutral-800">
              To build a future where farmers and rural communities thrive through sustainable, transparent, and
              technology-driven cooperative ecosystems—ensuring access to resources, markets, and knowledge that
              drive inclusive prosperity.
            </p>

            {/* Quote strip */}
            <div className="mt-6 rounded-xl bg-gradient-to-r from-[var(--gov-blue)]/10 via-white to-[var(--gov-green)]/10 p-4 border border-neutral-200">
              <p className="text-sm md:text-base text-[var(--gov-navy)]">
                <span className="font-semibold">Core promise:</span> dignity for every farmer, accountability in governance,
                and value creation across the supply chain.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Mission (two-column bullets) */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="rounded-2xl border border-neutral-200 bg-white/85 backdrop-blur-md shadow-[0_12px_40px_rgba(0,0,0,0.06)] p-6 md:p-10"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="text-2xl">🎯</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--gov-blue)]">Our Mission</h2>
          </div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8 text-neutral-800"
          >
            <ul className="space-y-4">
              {[
                "Ensure fair pricing and competitive market access for every farmer.",
                "Drive adoption of sustainable, climate-smart farming technologies.",
                "Capacity building for rural youth & women in agri-enterprise.",
                "Strengthen democratic, transparent cooperative governance.",
              ].map((item, i) => (
                <motion.li key={i} variants={fadeUp} className="flex items-start">
                  <span className="mr-3 text-[var(--gov-blue)]">•</span>
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>
            <ul className="space-y-4">
              {[
                "Direct F2C/FPO supply chains to reduce middlemen exploitation.",
                "Develop storage, cold-chain & export logistics for better reach.",
                "Inclusive rural finance & microcredit for smallholders.",
                "Policy partnerships with Government & private sector.",
              ].map((item, i) => (
                <motion.li key={i} variants={fadeUp} className="flex items-start">
                  <span className="mr-3 text-[var(--gov-blue)]">•</span>
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </motion.section>

        {/* Strategic Pillars */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-8"
        >
          <h3 className="text-xl md:text-2xl font-extrabold text-[var(--gov-navy)] mb-4">Strategic Pillars</h3>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: "🔒", title: "Transparency", text: "Open registers, digital workflows & member dashboards." },
              { icon: "♻️", title: "Sustainability", text: "Water-smart, soil-safe, climate-resilient practices." },
              { icon: "🚚", title: "Logistics", text: "Storage, transport & export readiness for scale." },
              { icon: "🤝", title: "Inclusivity", text: "Women, SC/ST & youth leadership in cooperatives." },
            ].map((c, i) => (
              <motion.div
                key={c.title}
                variants={fadeUp}
                className="rounded-xl border border-neutral-200 bg-white shadow hover:shadow-md transition p-5"
              >
                <div className="text-2xl">{c.icon}</div>
                <div className="mt-2 font-bold text-[var(--gov-navy)]">{c.title}</div>
                <p className="text-sm text-neutral-700 mt-1">{c.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* KPIs */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-8"
        >
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { label: "Registered Members", value: "Loading...", color: "from-[var(--gov-green)] to-[var(--gov-green-dark)]" },
              { label: "Farmers Trained", value: "Loading...", color: "from-sky-600 to-sky-700" },
              { label: "Storage Capacity", value: "Loading...", color: "from-amber-600 to-orange-700" },
              { label: "Villages Served", value: "Loading...", color: "from-[var(--gov-blue)] to-[var(--gov-blue-deep)]" },
            ].map((k) => (
              <motion.div
                key={k.label}
                variants={fadeUp}
                className="rounded-xl border border-neutral-200 bg-white shadow overflow-hidden"
              >
                <div className={`h-1.5 bg-gradient-to-r ${k.color}`} />
                <div className="p-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">{k.label}</div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="mt-1 text-2xl md:text-3xl font-extrabold text-[var(--gov-navy)]"
                  >
                    {k.value}
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* CTA Band */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-10"
        >
          <div className="rounded-2xl p-5 md:p-7 bg-gradient-to-r from-[var(--gov-green)] via-emerald-600 to-[var(--gov-blue)]
                          text-white shadow-[0_12px_40px_rgba(0,0,0,0.12)] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xl md:text-2xl font-extrabold">Let’s build a cooperative future together</div>
              <p className="text-white/90 text-sm md:text-base">
                Become a member or reach out to collaborate on storage, logistics and export initiatives.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/apply"
                className="px-4 py-2 rounded-full bg-white text-[var(--gov-navy)] font-semibold shadow hover:opacity-95"
              >
                Apply Membership
              </Link>
              <Link
                to="/contact/HeadOffice"
                className="px-4 py-2 rounded-full border border-white/70 font-semibold hover:bg-white/10"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
